/**
 * Blog posts as the backend stores them (tenant `blogs`: title, slug, status, banner and body as Media files,
 * read_duration, excerpt, published_at) and the shapes the storefront and admin use. The body is an HTML file
 * in the media library; the excerpt and reading time come from the stored fields when set, else from the body.
 */

export const BLOG_STATUS_PUBLISHED = "published";
export const BLOG_STATUS_DRAFT = "draft";
export type BlogStatus = typeof BLOG_STATUS_PUBLISHED | typeof BLOG_STATUS_DRAFT;

/** A post as `GET /api/blog` and `/api/backoffice/blog` return it with BLOG_GRAPH. */
export interface BlogPostDto {
  _id: string;
  title: string;
  slug: string;
  status: string;
  read_duration?: string | null;
  /** Merchant-written summary (≤ 300 characters); older posts have none. */
  excerpt?: string | null;
  /** When the post goes live: set on first publish, or chosen (a future time schedules it). Older posts have none. */
  published_at?: string | null;
  category?: string | null;
  tags?: string[] | null;
  author_name?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  banner_id?: string | null;
  content_id?: string | null;
  banner?: { _id?: string; path?: string | null } | null;
  content?: { _id?: string; path?: string | null } | null;
  created_at: string;
  updated_at?: string;
}

/** Fields the storefront and admin ask for: the post and the storage paths of its banner and body. */
export const BLOG_GRAPH = { "*": true, banner: { _id: true, path: true }, content: { _id: true, path: true } };

/** A post in a list: everything but the body. `image` is a storage path (resolve with the CDN base). */
export interface BlogCard {
  id: string;
  slug: string;
  title: string;
  image: string | null;
  date: string;
  readMinutes: number;
  excerpt: string;
  category: string;
  tags: string[];
  author: string;
}

/** A post with its body (unsanitised merchant HTML: render it through the storefront's SafeHtml). */
export interface BlogArticle extends BlogCard {
  html: string;
  updatedAt: string;
  /** For the page title and meta description; fall back to the title and excerpt. */
  seoTitle: string;
  seoDescription: string;
}

export const isPublished = (post: Pick<BlogPostDto, "status">): boolean => post.status === BLOG_STATUS_PUBLISHED;

/** Published and its publish time (when set) has come — a scheduled post is not live yet. */
export const isLive = (post: Pick<BlogPostDto, "status" | "published_at">, now: Date = new Date()): boolean =>
  isPublished(post) && (!post.published_at || new Date(post.published_at).getTime() <= now.getTime());

/** Text of an HTML fragment: tags removed, entities for spaces/quotes/ampersands decoded, whitespace collapsed. */
export const stripHtml = (html: string): string =>
  html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

/** The first paragraph of the body as plain text, cut at a word boundary to `max` characters. */
export const excerptOf = (html: string, max = 160): string => {
  const first = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i)?.[1] ?? html;
  const text = stripHtml(first) || stripHtml(html);
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 0) || max).trimEnd()}…`;
};

/** Minutes to read: the merchant's `read_duration` when it holds a number, else words / 200 (at least 1). */
export const readMinutes = (html: string, stated?: string | null): number => {
  const given = Number.parseInt(String(stated ?? ""), 10);
  if (Number.isFinite(given) && given > 0) return given;
  const words = stripHtml(html).split(" ").filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

/** Whether a list card has to read the body file: only when the post stores no excerpt or no reading time. */
export const cardNeedsBody = (post: BlogPostDto): boolean => !post.excerpt?.trim() || !(Number.parseInt(String(post.read_duration ?? ""), 10) > 0);

/** A list card from a backend post and (when already loaded) its body. Stored excerpt and publish date win. */
export const toBlogCard = (post: BlogPostDto, html = ""): BlogCard => ({
  id: post._id,
  slug: post.slug,
  title: post.title,
  image: post.banner?.path ?? null,
  date: post.published_at || post.created_at,
  readMinutes: readMinutes(html, post.read_duration),
  excerpt: post.excerpt?.trim() || (html ? excerptOf(html) : ""),
  category: post.category?.trim() ?? "",
  tags: (post.tags ?? []).filter(Boolean),
  author: post.author_name?.trim() ?? "",
});

export const toBlogArticle = (post: BlogPostDto, html: string): BlogArticle => ({
  ...toBlogCard(post, html),
  html,
  updatedAt: post.updated_at ?? post.created_at,
  seoTitle: post.seo_title?.trim() || post.title,
  seoDescription: post.seo_description?.trim() || post.excerpt?.trim() || (html ? excerptOf(html) : ""),
});
